import { tenantA4Letter as phmA4Letter } from '../../../assets/letter/tenants/phm/a4_letter';
import { tenantA4Letter2nd as phmA4Letter2nd } from '../../../assets/letter/tenants/phm/a4_letter_2nd_page';
import { tenantJobLabel as phmJobLabel } from '../../../assets/letter/tenants/phm/job_label';
import { tenantA4Letter as ndtA4Letter } from '../../../assets/letter/tenants/ndt/a4_letter';
import { tenantA4Letter2nd as ndtA4Letter2nd } from '../../../assets/letter/tenants/ndt/a4_letter_2nd_page';
import { tenantJobLabel as ndtJobLabel } from '../../../assets/letter/tenants/ndt/job_label';
import { tenantA4Letter as demoA4Letter } from '../../../assets/letter/tenants/demo/a4_letter';
import { tenantA4Letter2nd as demoA4Letter2nd } from '../../../assets/letter/tenants/demo/a4_letter_2nd_page';
import { tenantJobLabel as demoJobLabel } from '../../../assets/letter/tenants/demo/job_label';

export type CrmProvider = 'legacy' | 'hubspot';

export interface TenantBrandingAssets {
  appTitle: string;
  logoNavbar: string;
  logoLogin: string;
  loginBackground: string;
  fontFamily: string;
  loginBackgroundPosition: string;
  loginBackgroundSize: string;
}

export interface TenantLetterAssets {
  a4Letter: any;
  a4Letter2nd: any;
  jobLabel: any;
}

export interface TenantConfig {
  id: string;
  displayName: string;
  crmProvider: CrmProvider;
  hostPatterns: string[];
  legalCompanyName: string;
  letterCity: string;
  branding: TenantBrandingAssets;
  letterAssets: TenantLetterAssets;
}

const SHARED_PHM_LETTERS: TenantLetterAssets = {
  a4Letter: phmA4Letter,
  a4Letter2nd: phmA4Letter2nd,
  jobLabel: phmJobLabel,
};

const SHARED_NDT_LETTERS: TenantLetterAssets = {
  a4Letter: ndtA4Letter,
  a4Letter2nd: ndtA4Letter2nd,
  jobLabel: ndtJobLabel,
};

const SHARED_DEMO_LETTERS: TenantLetterAssets = {
  a4Letter: demoA4Letter,
  a4Letter2nd: demoA4Letter2nd,
  jobLabel: demoJobLabel,
};

const SHARED_PHM_BRANDING: TenantBrandingAssets = {
  appTitle: 'PHM PPS',
  logoNavbar: '/assets/images/tenants/phm/logo-navbar.png',
  logoLogin: '/assets/images/tenants/phm/login-logo.png',
  loginBackground: '/assets/images/tenants/phm/login-bg.svg',
  fontFamily: '"Segoe UI", "Helvetica Neue", Arial, sans-serif',
  loginBackgroundPosition: '25.2rem 0',
  loginBackgroundSize: '80%',
};

const TENANT_CONFIGS: Record<string, TenantConfig> = {
  phm: {
    id: 'phm',
    displayName: 'PHM PPS x',
    crmProvider: 'legacy',
    hostPatterns: [
      'localhost',
      '127.0.0.1',
      'dev-pps.wmedia.de',
      'uat-phm.portxx.de',
      'phm.portxx.de',
    ],
    legalCompanyName: 'PHM Industrieanlagen GmbH',
    letterCity: 'Burg',
    branding: SHARED_PHM_BRANDING,
    letterAssets: SHARED_PHM_LETTERS,
  },
  demo: {
    id: 'demo',
    displayName: 'PPS Demo',
    crmProvider: 'legacy',
    hostPatterns: ['pps-demo.wmedia-di.de'],
    legalCompanyName: 'The PPD Demo GmbH',
    letterCity: 'Magdeburg',
    branding: {
      appTitle: 'PPS Demo',
      logoNavbar: '/assets/images/tenants/demo/logo-demo-navbar.png',
      logoLogin: '/assets/images/tenants/demo/logo-login.png',
      loginBackground: '/assets/images/tenants/demo/login-bg-2.svg',
      fontFamily: '"Segoe UI", "Helvetica Neue", Arial, sans-serif',
      loginBackgroundPosition: '68& top',
      loginBackgroundSize: 'cover',
    },
    letterAssets: SHARED_DEMO_LETTERS,
  },
  ndt: {
    id: 'ndt',
    displayName: 'NDT PPS',
    crmProvider: 'hubspot',
    hostPatterns: ['pps.ndt-division.de', 'pps-uat.ndt-division.de'],
    legalCompanyName: 'NDT Division GmbH',
    letterCity: 'Magdeburg',
    branding: {
      appTitle: 'NDT PPS',
      logoNavbar: '/assets/images/tenants/ndt/logo-navbar.png',
      logoLogin: '/assets/images/tenants/ndt/login-logo.svg',
      loginBackground: '/assets/images/tenants/ndt/login-bg.svg',
      fontFamily: '"Inter", "Segoe UI", "Helvetica Neue", Arial, sans-serif',
      loginBackgroundPosition: '68% top',
      loginBackgroundSize: 'cover',
    },
    letterAssets: SHARED_NDT_LETTERS,
  },
  'ndt-dev': {
    id: 'ndt-dev',
    displayName: 'NDT PPS (DEV)',
    crmProvider: 'hubspot',
    hostPatterns: ['pps-dev.ndt-division.de'],
    legalCompanyName: 'NDT Division GmbH',
    letterCity: 'Magdeburg',
    branding: {
      appTitle: 'NDT PPS (DEV)',
      logoNavbar: '/assets/images/tenants/ndt/logo-navbar.png',
      logoLogin: '/assets/images/tenants/ndt/login-logo.svg',
      loginBackground: '/assets/images/tenants/ndt/login-bg.svg',
      fontFamily: '"Inter", "Segoe UI", "Helvetica Neue", Arial, sans-serif',
      loginBackgroundPosition: '68% top',
      loginBackgroundSize: 'cover',
    },
    letterAssets: SHARED_NDT_LETTERS,
  },
};

const TENANT_ALIASES: Record<string, string> = {
  default: 'phm',
  pps: 'phm',
  'pps-demo': 'demo',
  ndtdivision: 'ndt',
  'ndt-division': 'ndt',
};

export const DEFAULT_TENANT_ID = 'phm';

export function normalizeTenantId(candidate?: string | null): string | undefined {
  if (!candidate) {
    return undefined;
  }
  const normalized = candidate.trim().toLowerCase();
  if (!normalized) {
    return undefined;
  }
  return TENANT_ALIASES[normalized] ?? normalized;
}

export function getTenantConfigById(candidate?: string | null): TenantConfig | undefined {
  const normalized = normalizeTenantId(candidate);
  if (!normalized) {
    return undefined;
  }
  return TENANT_CONFIGS[normalized];
}

function hostMatchesPattern(hostname: string, pattern: string): boolean {
  const normalizedHost = hostname.toLowerCase();
  const normalizedPattern = pattern.toLowerCase();

  if (normalizedPattern.startsWith('*.')) {
    const suffix = normalizedPattern.slice(2);
    return normalizedHost === suffix || normalizedHost.endsWith(`.${suffix}`);
  }

  return normalizedHost === normalizedPattern;
}

export function getTenantConfigForHost(hostname?: string | null): TenantConfig | undefined {
  if (!hostname) {
    return undefined;
  }
  const normalizedHost = hostname.trim().toLowerCase();
  if (!normalizedHost) {
    return undefined;
  }

  for (const config of Object.values(TENANT_CONFIGS)) {
    if (config.hostPatterns.some(pattern => hostMatchesPattern(normalizedHost, pattern))) {
      return config;
    }
  }

  return undefined;
}

export function getDefaultTenantConfig(): TenantConfig {
  return TENANT_CONFIGS[DEFAULT_TENANT_ID];
}
